import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import SingleVault from './SingleVault';
import BottomDrawer, { DrawerButtonsWrapper } from '../Drawer/Drawer';
import { ReactComponent as ArrowRight } from '../../assets/images/svg/arrow-right.svg';
import Button from '../Button/Button';
import SectionLoader from '../Loader/SectionLoader';
import InfoPopup from './components/InfoPopup';
import themeColors from '../../assets/styles/themeColors';
import { pxToRem } from '../../assets/styles/helper';
import Paragraph from '../Paragraph/Paragraph';
import { GS_TARIFF_LINK, HAA_TARIFF_LINK } from '../../util/constants';
import config from '../../config';

const ContentWrapper = styled.div`
  > div {
    min-height: 130px;
  }
`;

const TariffInfoContent = styled.div`
  display: flex;
  flex-direction: column;

  > p {
    color: ${themeColors.colorTextPrimary};
    font-size: ${pxToRem(16)};
    font-weight: 400;
    margin-bottom: 2px;
  }
`;

const TarifInfoLink = styled.a`
  color: ${themeColors.colorTextSecondary};
  font-size: ${pxToRem(12)};
  font-weight: 400;
  text-decoration: underline;
  cursor: pointer;
`;

const SelectVaultLocation = ({
  open,
  setOpen,
  wizardData,
  setWizardData,
  lowestVault,
  setOpenSelectStorageType,
  sortedLocations,
  isLoading,
  locations,
  isIRAAccount,
}) => {
  const { t } = useTranslation();
  const { brokerTheme } = config;
  const isGS = brokerTheme === 'GoldSilver';
  const [isTariffPopupVisible, setIsTariffPopupVisible] = useState(false);

  const isVaultSelected = (vault) => {
    if (!wizardData?.selectedVault) return false;
    return wizardData.selectedVault?.LocationType === vault.LocationType;
  };

  const selectVault = (vault) => {
    const matchedLocation = locations.find(
      (loc) => loc.LocationType === vault.LocationType,
    );

    const mergedVault = { ...matchedLocation, ...vault };

    const allowsSeg = !!mergedVault.IsAvailableForSegregated;
    const newPlan = allowsSeg
      ? wizardData?.selectedPlan || 'standard'
      : 'standard';

    setWizardData({
      ...wizardData,
      selectedVault: mergedVault,
      selectedPlan: newPlan,
    });

    setIsTariffPopupVisible(Boolean(mergedVault.IsTariffActive));
  };

  const continueWithStandardStorage = () => {
    setWizardData({
      ...wizardData,
      selectedPlan: 'standard',
    });

    setOpen(false);
  };

  const goToSelectStorageType = () => {
    // setOpen(false);
    setOpenSelectStorageType(true);
  };

  return (
    <BottomDrawer
      isOpen={open}
      onClose={() => setOpen(false)}
      title={t('buyWizard.selectVaultLocationTitle')}
      subtitle={t('buyWizard.selectVaultLocationSubtitle')}
      footer={
        <DrawerButtonsWrapper>
          <Button
            variant="primaryOutlined"
            textTransform="none"
            onClick={continueWithStandardStorage}
          >
            {t('buyWizard.continueWithStandardStorage')}
          </Button>
          <Button
            variant="primary"
            textTransform="none"
            onClick={goToSelectStorageType}
            disabled={
              !wizardData?.selectedVault?.IsAvailableForSegregated ||
              isIRAAccount
            }
          >
            {t('buyWizard.compareStorageOptions')}
            <ArrowRight />
          </Button>
        </DrawerButtonsWrapper>
      }
    >
      <>
        {wizardData?.selectedVault?.IsTariffActive && isTariffPopupVisible && (
          <InfoPopup onClose={() => setIsTariffPopupVisible(false)}>
            <TariffInfoContent>
              <Paragraph>{t('buyWizard.tariffInfoMessage')}</Paragraph>
              <TarifInfoLink
                href={isGS ? GS_TARIFF_LINK : HAA_TARIFF_LINK}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('buyWizard.learnMore')}
              </TarifInfoLink>
            </TariffInfoContent>
          </InfoPopup>
        )}

        <ContentWrapper>
          <SectionLoader isLoading={isLoading}>
            {sortedLocations.map((location) => (
              <SingleVault
                key={location?.LocationType}
                isTestProduct={location.IsTestProduct}
                isVaultSelected={isVaultSelected(location)}
                selectVault={() => selectVault(location)}
                vault={location}
                isLowestPriceVault={lowestVault}
              />
            ))}
          </SectionLoader>
        </ContentWrapper>
      </>
    </BottomDrawer>
  );
};

SelectVaultLocation.propTypes = {
  wizardData: PropTypes.shape({
    storageProduct: PropTypes.shape({
      Locations: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    selectedVault: PropTypes.shape({
      LocationType: PropTypes.number,
      IsAvailableForSegregated: PropTypes.bool,
      IsTariffActive: PropTypes.bool,
    }),
    selectedPlan: PropTypes.string,
  }),
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  setWizardData: PropTypes.func,
  lowestVault: PropTypes.shape({}),
  setOpenSelectStorageType: PropTypes.func,
  sortedLocations: PropTypes.arrayOf(PropTypes.shape({})),
  isLoading: PropTypes.bool,
  locations: PropTypes.arrayOf(PropTypes.shape({})),
  isIRAAccount: PropTypes.bool,
};

export default SelectVaultLocation;
